import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { Workflow, loadWorkflowCanvas } from '../../store/project/projectSlice';
import { extractParamRefs } from '../../types/workflowSettings';
import { FolderNode, buildFolderTree, loadEmptyFolders } from '../../utils/workflowFolders';
import { showAlert, showConfirm } from '../common/AppDialog';
import WorkflowLoadNotice from '../common/WorkflowLoadNotice';
import RunParamsDialog from '../workflow/RunParamsDialog';
import {
  DagSummary, fetchSummary, triggerRun, pollRun, stopRun,
  fmtSchedule, fmtTime, fmtDuration, stateLabel, stateStyle, dagIdOf, isActiveState,
} from '../../services/executionApi';
import { useTranslation } from 'react-i18next';

/** '이력' 이 왼쪽에 넘기는 범위 — 워크플로 하나이거나 폴더·프로젝트 전체 */
export interface HistoryScope { ids: string[]; label: string }

interface WorkflowTreeProps {
  selectedWorkflowId?: string | null;
  onSelectWorkflow: (workflow: Workflow) => void;
  /** 실행이 끝나면 이력·달력이 다시 읽도록 */
  onRunFinished?: (workflowId: string) => void;
  /** '이력' — 왼쪽 그래프·실행 목록을 이 범위로 좁힌다. 기간은 그래프에 설정된 것을 그대로 쓴다 */
  onShowHistory?: (scope: HistoryScope) => void;
  /** 지금 왼쪽에서 좁혀 보고 있는 워크플로 id 들 — 트리에서 표시한다 */
  focusIds?: string[] | null;
}

/**
 * 실행 탭 워크플로 트리 — 디자이너 탐색기와 같은 계층(프로젝트 > 폴더/그룹 > 워크플로).
 *
 * **이 화면은 워크플로를 바꾸지 않는다.** 스케줄은 배포된 DAG 의 값을 보여 주기만 하고,
 * 실행은 이미 배포된 DAG 만 트리거한다. 폴더는 별도 엔티티가 아니라 워크플로의 folder 경로에서 유추한다
 * (utils/workflowFolders — 디자이너 탐색기와 같은 트리 함수를 쓴다).
 */
const WorkflowTree: React.FC<WorkflowTreeProps> = ({ selectedWorkflowId, onSelectWorkflow, onRunFinished, onShowHistory, focusIds }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const selectedProjectId = useSelector((s: RootState) => s.project.selectedProjectId);
  const projects = useSelector((s: RootState) => s.project.projects);
  const workflows = useSelector((s: RootState) => s.project.workflows);
  const project = projects.find((p) => p.id === selectedProjectId);

  const projectWorkflows = useMemo(
    () => workflows.filter((w) => w.projectId === selectedProjectId),
    [workflows, selectedProjectId],
  );
  // 빈 폴더는 디자이너에서 만든 것이 브라우저에 기억돼 있다 — 같은 트리를 보여 주려고 함께 읽는다
  const tree = useMemo(
    () => buildFolderTree(projectWorkflows, selectedProjectId ? loadEmptyFolders(selectedProjectId) : []),
    [projectWorkflows, selectedProjectId],
  );

  const [summary, setSummary] = useState<Record<string, DagSummary>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [projectOpen, setProjectOpen] = useState(true);
  /** 실행 중인 워크플로 — id → 화면에 보일 상태 문구 */
  const [running, setRunning] = useState<Record<string, string>>({});
  const [paramsFor, setParamsFor] = useState<{ workflow: Workflow; refs: string[] } | null>(null);
  /** 중지를 누른 워크플로 — 응답이 올 때까지 버튼을 잠근다 */
  const [stopping, setStopping] = useState<string | null>(null);

  /** 도는 실행을 즉시 중지 — 이 화면에서 시작한 것이 아니어도(스케줄·다른 세션) 멈출 수 있다 */
  const stopIt = async (w: Workflow, runId: string) => {
    if (!(await showConfirm(t('execution:tree.stopConfirm', { name: w.name }), { title: t('execution:tree.stopTitle'), okText: t('execution:tree.stopOk') }))) return;
    setStopping(w.id);
    try {
      await stopRun(w.id, runId);
      await refresh();
      onRunFinished?.(w.id);
    } catch (e) {
      showAlert(t('execution:tree.stopFailed', { message: (e as Error).message }), { title: w.name, tone: 'danger' });
    } finally {
      setStopping(null);
    }
  };
  // StrictMode 는 개발에서 이펙트를 두 번 돌린다 — ref 를 다시 true 로 돌려놓지 않으면 이후 상태 갱신이 무시된다
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const refresh = useCallback(async () => {
    if (!projectWorkflows.length) { setSummary({}); return; }
    setLoading(true);
    try {
      const s = await fetchSummary(projectWorkflows.map((w) => w.id));
      if (alive.current) { setSummary(s); setLoadError(''); }
    } catch (e) {
      if (alive.current) setLoadError((e as Error).message);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [projectWorkflows]);

  useEffect(() => { refresh(); }, [refresh]);

  const infoOf = (w: Workflow): DagSummary | undefined => summary[dagIdOf(w.id)];
  const focus = useMemo(() => new Set(focusIds || []), [focusIds]);

  /** 실행 — 캔버스는 ${변수} 를 알아내려고 읽기만 한다(저장·배포 없음) */
  const startRun = async (w: Workflow, conf: Record<string, string>) => {
    setRunning((p) => ({ ...p, [w.id]: t('execution:tree.triggering') }));
    try {
      const runId = await triggerRun(w.id, conf);
      setRunning((p) => ({ ...p, [w.id]: t('execution:tree.running') }));
      const state = await pollRun(w.id, runId, (st, tasks) => {
        if (!alive.current) return;
        const done = tasks.filter((task) => task.state === 'success').length;
        setRunning((p) => ({ ...p, [w.id]: `${stateLabel(st)} (${done}/${tasks.length})` }));
      });
      if (!alive.current) return;
      setRunning((p) => { const n = { ...p }; delete n[w.id]; return n; });
      await refresh();
      onRunFinished?.(w.id);
      if (state !== 'success') showAlert(t('execution:tree.endedWith', { state: stateLabel(state) }), { title: w.name });
    } catch (e) {
      if (!alive.current) return;
      setRunning((p) => { const n = { ...p }; delete n[w.id]; return n; });
      showAlert(t('execution:tree.runFailed', { message: (e as Error).message }), { title: w.name, tone: 'danger' });
    }
  };

  const handleRunClick = async (w: Workflow) => {
    const info = infoOf(w);
    if (!info?.deployed) {
      showAlert(t('execution:tree.notDeployed'), { title: w.name });
      return;
    }
    let nodes = w.nodes;
    if (nodes === undefined) {
      const r = await dispatch(loadWorkflowCanvas(w.id));
      if (loadWorkflowCanvas.rejected.match(r)) { showAlert(t('execution:tree.loadCanvasFailed', { message: r.error?.message || '' })); return; }
      nodes = (r.payload as any).nodes;
    }
    const refs = extractParamRefs(nodes || []);
    if (refs.length) setParamsFor({ workflow: w, refs });
    else startRun(w, {});
  };

  /** 폴더(와 그 아래 폴더) 안의 워크플로 전부 */
  const workflowsOf = (node: FolderNode): Workflow[] => [
    ...node.workflows,
    ...node.children.flatMap((c) => workflowsOf(c)),
  ];

  const historyBtn = (ids: string[], label: string, title: string) => (
    <button
      type="button"
      className="px-2 py-0.5 border border-gray-300 text-gray-600 rounded text-xs hover:bg-gray-50 disabled:opacity-40"
      disabled={ids.length === 0}
      title={title}
      onClick={(e) => { e.stopPropagation(); onShowHistory?.({ ids, label }); }}
    >
      {t('execution:tree.history')}
    </button>
  );

  /** 워크플로 한 줄 */
  const renderWorkflow = (w: Workflow, depth: number) => {
    const info = infoOf(w);
    const last = info?.last_run;
    const busy = running[w.id];
    const selected = selectedWorkflowId === w.id;
    return (
      <div
        key={w.id}
        className={`group flex items-center gap-1.5 pr-2 py-1 cursor-pointer ${selected ? 'bg-blue-50' : focus.has(w.id) ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}
        onClick={() => onSelectWorkflow(w)}
      >
        <span className="shrink-0">{w.icon || '📄'}</span>
        <span className="text-sm text-gray-800 truncate max-w-[200px]" title={w.description || w.name}>{w.name}</span>

        {!info ? <span className="text-xs text-gray-400">{t('execution:tree.reading')}</span>
          : !info.deployed ? <span className="text-xs text-gray-400">{t('execution:tree.undeployed')}</span>
            : !last ? <span className="text-xs text-gray-400">{t('execution:tree.noRuns')}</span>
              : (
                <span className="inline-flex items-center gap-1 text-xs min-w-0">
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${stateStyle(last.state)}`}>{stateLabel(last.state)}</span>
                  <span className="text-gray-600 truncate" title={t('execution:tree.startedAt', { time: fmtTime(last.start_date) })}>
                    {isActiveState(last.state) ? fmtTime(last.start_date).slice(5, 16) : fmtTime(last.end_date || last.start_date).slice(5, 16)}
                  </span>
                  <span className="text-gray-400 shrink-0">{isActiveState(last.state) ? t('execution:tree.inProgress') : fmtDuration(last.start_date, last.end_date)}</span>
                </span>
              )}

        {info?.deployed && (
          <span className="text-xs text-gray-400 truncate hidden xl:inline" title={t('execution:tree.scheduleTitle')}>
            {fmtSchedule(info.schedule_interval)}{info.is_paused ? t('execution:tree.paused') : ''}
          </span>
        )}

        <span className="ml-auto flex items-center gap-1 shrink-0">
          {/* 도는 실행이 있으면 중지 — 이 화면에서 시작한 것이든, 스케줄·다른 세션이 돌린 것이든 */}
          {last && isActiveState(last.state) && last.dag_run_id && (
            <button
              className="px-2 py-0.5 border border-red-300 text-red-700 rounded text-xs hover:bg-red-50 disabled:opacity-40"
              disabled={stopping === w.id}
              title={t('execution:tree.stopHint')}
              onClick={(e) => { e.stopPropagation(); stopIt(w, last.dag_run_id); }}
            >
              {stopping === w.id ? t('execution:tree.stopping') : t('execution:tree.stop')}
            </button>
          )}
          {busy ? (
            <span className="px-2 py-0.5 inline-flex items-center gap-1 rounded bg-blue-50 text-blue-700 text-xs">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />{busy}
            </span>
          ) : (
            <button
              className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 disabled:bg-gray-100 disabled:text-gray-400"
              disabled={!info?.deployed}
              title={info?.deployed ? t('execution:tree.runNow') : t('execution:tree.deployFirst')}
              onClick={(e) => { e.stopPropagation(); handleRunClick(w); }}
            >
              {t('execution:tree.run')}
            </button>
          )}
          {historyBtn([w.id], w.name, t('execution:tree.historyWorkflow'))}
        </span>
      </div>
    );
  };

  /** 폴더 한 줄 + 그 아래 (재귀) */
  const renderFolder = (node: FolderNode, depth: number): React.ReactNode => {
    const open = !collapsed[node.path];
    const ids = workflowsOf(node).map((w) => w.id);
    return (
      <div key={node.path || '(root)'}>
        <div
          className="group flex items-center gap-1.5 pr-2 py-1 cursor-pointer hover:bg-gray-50"
          style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}
          onClick={() => setCollapsed((p) => ({ ...p, [node.path]: open }))}
        >
          <span className="w-3 text-gray-400 shrink-0">{open ? '▾' : '▸'}</span>
          <span className="shrink-0">📁</span>
          <span className="text-sm text-gray-700 truncate">{node.name}</span>
          <span className="text-xs text-gray-400">{node.total}</span>
          <span className="ml-auto shrink-0">
            {historyBtn(ids, node.name, t('execution:tree.historyFolder'))}
          </span>
        </div>
        {open && (
          <div>
            {node.children.map((c) => renderFolder(c, depth + 1))}
            {node.workflows.map((w) => renderWorkflow(w, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-3 border-b border-gray-200 flex items-center gap-2">
        <span className="font-medium">{t('execution:tree.title')}</span>
        <span className="text-xs text-gray-500 truncate">
          {t('execution:tree.intro')}
        </span>
        <button
          className="ml-auto px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 shrink-0"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? t('execution:tree.reading') : t('execution:tree.refresh')}
        </button>
      </div>

      {loadError && (
        <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100">
          {t('execution:tree.loadFailed', { message: loadError })}
        </div>
      )}

      <div className="max-h-[620px] overflow-auto">
        {projectWorkflows.length === 0 ? (
          <div className="p-6 text-center">
            {/* 목록을 못 읽은 것과 정말 없는 것은 다르다 — 그 둘을 갈라 말한다 */}
            <WorkflowLoadNotice emptyHint={<span className="text-sm text-gray-500">{t('execution:tree.empty')}</span>} />
          </div>
        ) : (
          <div className="py-1">
            {/* 프로젝트 — 트리의 뿌리. '이력' 은 프로젝트 전체를 왼쪽에 모아 보여 준다 */}
            <div className="group flex items-center gap-1.5 pr-2 py-1 cursor-pointer hover:bg-gray-50" onClick={() => setProjectOpen((v) => !v)}>
              <span className="w-3 text-gray-400 shrink-0">{projectOpen ? '▾' : '▸'}</span>
              <span className="shrink-0">🗂</span>
              <span className="text-sm font-medium text-gray-800 truncate">{project?.name || t('execution:tree.project')}</span>
              <span className="text-xs text-gray-400">{projectWorkflows.length}</span>
              <span className="ml-auto shrink-0">
                {historyBtn(projectWorkflows.map((w) => w.id), project?.name || t('execution:tree.project'), t('execution:tree.historyProject'))}
              </span>
            </div>
            {projectOpen && (
              <div>
                {tree.children.map((c) => renderFolder(c, 1))}
                {tree.workflows.map((w) => renderWorkflow(w, 1))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 실행 파라미터 — 디자이너의 실행과 같은 팝업 (캔버스가 ${변수} 를 참조할 때만) */}
      {paramsFor && (
        <RunParamsDialog
          refs={paramsFor.refs}
          onClose={() => setParamsFor(null)}
          onRun={(conf) => { const w = paramsFor.workflow; setParamsFor(null); startRun(w, conf); }}
        />
      )}
    </div>
  );
};

export default WorkflowTree;
